// <reference types="paypal-checkout-components" />
// Type assertion to tell TypeScript that 'paypal' and 'navigator' exists on 'Window'
declare let window: Window & { paypal: any, navigator: any, ApplePaySession: any };

import { Component, OnInit, NgZone, EventEmitter, Input, Output, ViewChild, ElementRef, AfterContentInit, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { PaymentService } from '../../services/payment.service';
import { ReferenceService } from '../../services/reference.service';

import { PaymentSelectType, PaymentSelected } from '../payment-note-list/payment-note-list.component';
import { PaymentAddress, PaymentDetail, PaymentType, BankAccountType, BraintreeCreditCardType, GooglePayConfig, ApplePayConfig } from '../../models/payment';
import { StatesListItem, CountryListItem, VerifyListItem, CardExpireMonthYearListItem } from '../../models/reference';
import { CompanyInformation } from '../../models/company';          // 10/11/2021
import { CompanyService } from '../../services/company.service';    // 10/11/2021

import * as braintree from 'braintree-web';
import { BraintreeService } from 'src/app/services/braintree.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { ButtonColorOption, ButtonSizeOption, FlowType, Window } from '../paypal/enums';
import {take} from 'rxjs/operators';

@Component({
    selector: 'app-payment-form',
    templateUrl: './payment-form.component.html',
    styleUrls: ['./payment-form.component.scss']
})
export class PaymentFormComponent implements OnInit {
    private _participantId: number = null;
    private _paymentAmount: number = null;
    private companyCode: string = null;                               // 10/11/2021
    private _paymentSelected: PaymentSelected = {
        paymentSelectType: null,
        selectedId: null,
        selectedData: []
    };

    // Required to enable/disable 'Submit Payment' button (Mixture of Reactive controls and non-Reactive controls)
    @ViewChild('SubmitButton', { read: ElementRef, static: false }) SubmitButton: ElementRef<HTMLButtonElement>;

    @ViewChild('autosize') autosize: CdkTextareaAutosize;

    triggerResize() {
        // Wait for changes to be applied, then trigger textarea resize.
        this._ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize.resizeToFitContent(true));
    }

    @Input()
    set participantId(participantId: number) {
        this._participantId = participantId || null;
        this.getData();
    }
    get participantId(): number {
        return this._participantId;
    }

    @Input()
    set paymentAmount(paymentAmount: number) {
        this._paymentAmount = paymentAmount || null;
        this.getData();
    }
    get paymentAmount(): number {
        return this._paymentAmount;
    }

    @Input()
    set paymentSelected(paymentSelected: PaymentSelected) {
        this._paymentSelected = paymentSelected || {
            paymentSelectType: null,
            selectedId: null,
            selectedData: []
        };
        this.getData();

        // When changing notes or groups, let teardown Braintree's hosted fields allowing different program loan groups.
        // Also, if we don't tear down the fields, we will receive duplicate Iframe errors.
        if (this._paymentSelected.selectedData.length > 0) {
            if (this._brainTreeUIRenderComplete) {
                this._brainTreeUIRenderComplete = false;
                this._braintreeService.tearDownBraintree(this._hostedFieldsInstance);
            }
        }
    }
    get paymentSelected(): PaymentSelected {
        return this._paymentSelected;
    }

    @Output() dataComplete = new EventEmitter<any>();
    @Output() submitted = new EventEmitter<PaymentDetail>();
    @Output() canceled = new EventEmitter();

    private _handleError: HandleError;
    private _logging = environment.logging;

    isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

    fieldValidations = environment.fieldValidations;

    showSpinner = false;
    allowPaymentInfoEdit = false;
    paymentAddressFound = false;
    paymentAddress: PaymentAddress = null;
    noteIds: number[] = [];

    getPaymentAddressComplete = false;
    getCreditCardExpirationMonthListComplete = false;

    paymentType = PaymentType;
    bankAccountType = BankAccountType;

    getStateListComplete = false;
    stateList: StatesListItem[] = [];
    showStateListSpinner = false;

    // 03/30/2021 RA
    getCountryListComplete = false;
    countryList: CountryListItem[] = [];
    showCountryListSpinner = false;

    // 04/13/2021 RA
    verifySt_CntyListComplete = false;
    verifyst_cntry: VerifyListItem = null;

    creditCardExpirationMonthList: CardExpireMonthYearListItem[] = [];
    creditCardExpMonthList: string[] = [];
    showCreditCardExpirationMonthListSpinner = false;

    paymentForm: UntypedFormGroup;
    nameFormControl = new UntypedFormControl();
    addressLine1FormControl = new UntypedFormControl();
    addressLine2FormControl = new UntypedFormControl();
    cityFormControl = new UntypedFormControl();
    countryFormControl = new UntypedFormControl;
    stateFormControl = new UntypedFormControl();
    zipCodeFormControl = new UntypedFormControl();
    paymentTypeFormControl = new UntypedFormControl();
    bankRoutingNumberFormControl = new UntypedFormControl();
    bankAccountNumberFormControl = new UntypedFormControl();
    bankAccountNumberConfirmFormControl = new UntypedFormControl();
    bankAccountNumberEqualsFormControl = new UntypedFormControl();
    bankAccountTypeFormControl = new UntypedFormControl();
    companyInformation: CompanyInformation;            // 10/11/2021

    // Braintree HostedFields Support variables
    private _clientToken: string;
    private _hostedFieldsInstance?: braintree.HostedFields;
    private _payPalCheckoutInstance?: braintree.PayPalCheckout;
    private _applePayInstance?: braintree.ApplePay;
    private _googlePayInstance?: braintree.GooglePayment;
    private _dataCollectorInstance?: braintree.DataCollector;
    private _brainTreeLenderProgramCode: string;
    private _brainTreeFieldFocus: string;
    private _brainTreeUIRenderComplete: boolean = false;
    private _brainTreeLastPaymentType: PaymentType = null;
    private _brainTreeFieldsValid: boolean = false;
    public showBraintreeSpinner: boolean = false;
    private _isSubmitted: boolean = false;
    private _googlePayConfig?: GooglePayConfig;
    private _applePayConfig?: ApplePayConfig;
    public isApplePayBackendTest: boolean = true;
    public browserSupportsApplePay: boolean = true;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _errorHandler: ErrorHandler,
        private _userService: UserService,
        private _paymentService: PaymentService,
        private _referenceService: ReferenceService,
        public media: MediaObserver,
        private _companyService: CompanyService,            // 10/11/2021
        private _braintreeService: BraintreeService,
        private cdr: ChangeDetectorRef,
        private _ngZone: NgZone
    ) {
        this._handleError = _errorHandler.createHandleError('PaymentFormComponent');
        const currentDate = new Date();
    }

    ngOnInit() {
        this._googlePayConfig = this._braintreeService.getGooglePayConfig();
        this._applePayConfig = this._braintreeService.getApplePayConfig();
    }

    getData(): void {
        this.showSpinner = true;

        // 10/11/2021
        this.companyInformation = this._companyService.getNewCompanyInformation();
        this.companyCode = this._companyService.getCompanyCode();

        this._companyService.companyInformation.subscribe(
            response => (this.companyInformation = response),
            error => this._handleError('companyInformation.subscribe', error)
        );

        this.getStateList();
        this.getCountryList();                      // 03/30/2021 RA
        this.getPaymentAddress();                   // 03/30/2021 RA
        this.getCreditCardExpirationMonthList();
    }

    getStateList(): void {
        this.showSpinner = true;
        this.getStateListComplete = false;
        this.stateList = [];

        this._referenceService.getStatesList().subscribe(
            resultArray => {
                this.stateList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentFormComponent.getStateList :: resultArray.length= ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentFormComponent.getStateList :: resultArray = ' + JSON.stringify(resultArray));
                }
                this.getStateListComplete = true;
                // this.getPaymentAddress();            // 03/30/2021 RA Removed, moved up in code
            },
            error => this._handleError('getStateList', error)
        );
    }

    // 03/30/2021 RA Get State list filtered on Country Code
    getStateCountryList(country: string = null): void {
        this.getStateListComplete = false;
        this.stateList = [];

        this._referenceService.getStatesCountryList(country).subscribe(
            resultArray => {
                this.stateList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentFormComponent.getStateCountryList :: resultArray.length= ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentFormComponent.getStateCountryList :: resultArray = ' + JSON.stringify(resultArray));
                }
                this.getStateListComplete = true;
            },
            error => this._handleError('getStateCountryList', error)
        );
    }

    // 03/30/2021 RA
    getCountryList(): void {
        this.showSpinner = true;
        this.getCountryListComplete = false;
        this.countryList = [];

        this._referenceService.getCountryList().subscribe(
            resultArray => {
                this.countryList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentFormComponent.getCountryList :: resultArray.length= ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentFormComponent.getCountryList :: resultArray = ' + JSON.stringify(resultArray));
                }
                this.getCountryListComplete = true;
            },
            error => this._handleError('getCountryList', error)
        );
    }

    // 04/13/2021 RA Verify Country Code for State provided
    verifyState_Country(state: string = null, country: string = null): void {
        this.verifySt_CntyListComplete = false;

        this._referenceService.verifyStateCountry(state, country).subscribe(
            result => {
                this.verifySt_CntyListComplete = result !== null && result.countryCode != null;
                this.verifyst_cntry = result;
                // 04/13/2021 RA - To null out the state field if it's not valid
                if (this.verifySt_CntyListComplete === false) {
                    this.paymentForm.get('stateFormControl').setValue(null);
                } else {
                    // 04/19/2021 RA - Add state to field so it will be populated if valid
                    this.paymentForm.get('stateFormControl').setValue(state);
                }
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentFormComponent.verifyState_Country :: resultArray.length= ' + result);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentFormComponent.verifyState_Country :: resultArray = ' + JSON.stringify(result));
                }
            },
            error => this._handleError('verifyState_Country', error)
        );
    }

    getPaymentAddress(): void {
        this.showSpinner = true;
        this.getPaymentAddressComplete = false;
        if (
            this.participantId !== null &&
            this.paymentAmount !== null &&
            this._paymentSelected !== null &&
            this._paymentSelected.selectedData !== undefined &&
            this._paymentSelected.selectedData !== null
        ) {
            this.noteIds = [];
            for (const item of this._paymentSelected.selectedData) {
                this.noteIds.push(item.noteId);
            }

            this._paymentService.getPaymentAddress(this.participantId, this.noteIds).subscribe(
                result => {
                    this.paymentAddress = result;
                    if (this._logging.serviceCallTraceLog) {
                        console.log('PaymentFormComponent.getPaymentAddress :: result.noteIds= ' + result.noteIds);
                    }
                    if (this._logging.serviceDataTraceLog) {
                        console.log('PaymentFormComponent.getPaymentAddress :: result = ' + JSON.stringify(result));
                    }

                    this.paymentAddressFound = true;
                    this.getPaymentAddressComplete = true;
                    this.createForm();

                    this.getDataComplete();
                },
                error => this._handleError('getPaymentAddress', error)
            );
        } else {
            this.paymentAddressFound = false;
            this.paymentAddress = null;
            this.getDataComplete();
        }
    }

    getCreditCardExpirationMonthList(): void {
        this.showCreditCardExpirationMonthListSpinner = true;
        this.getCreditCardExpirationMonthListComplete = false;
        this.creditCardExpirationMonthList = [];

        this._referenceService.getCardExpireMonthList().subscribe(
            resultArray => {
                this.creditCardExpirationMonthList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentFormComponent.getCreditCardExpirationMonthList :: resultArray.length= ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentFormComponent.getCreditCardExpirationMonthList :: resultArray = ' + JSON.stringify(resultArray));
                }

                // Braintree requires an array of strings; months are prefixed with zero. Braintree are single digits
                resultArray.forEach(m => {
                    this.creditCardExpMonthList.push(m.expireValue);
                });

                this.showCreditCardExpirationMonthListSpinner = false;
                this.getCreditCardExpirationMonthListComplete = true;
                this.getDataComplete();
            },
            error => this._handleError('getCreditCardExpirationMonthList', error)
        );
    }

    getDataComplete(): void {
        this.showSpinner = false;

        if (
            this.getStateListComplete &&
            this.getCountryListComplete &&            // 03/30/2021 RA
            this.getPaymentAddressComplete &&
            this.getCreditCardExpirationMonthListComplete
        ) {
            this.dataComplete.emit(true);
        }
    }

    createForm(): void {
        this.paymentForm = this._formBuilder.group({});
        this.paymentForm.addControl('nameFormControl', this.nameFormControl);
        this.paymentForm.addControl('addressLine1FormControl', this.addressLine1FormControl);
        this.paymentForm.addControl('addressLine2FormControl', this.addressLine2FormControl);
        this.paymentForm.addControl('cityFormControl', this.cityFormControl);
        this.paymentForm.addControl('countryFormControl', this.countryFormControl);
        this.paymentForm.addControl('stateFormControl', this.stateFormControl);
        this.paymentForm.addControl('zipCodeFormControl', this.zipCodeFormControl);
        this.paymentForm.addControl('paymentTypeFormControl', this.paymentTypeFormControl);
        this.paymentForm.addControl('bankRoutingNumberFormControl', this.bankRoutingNumberFormControl);
        this.paymentForm.addControl('bankAccountNumberFormControl', this.bankAccountNumberFormControl);
        this.paymentForm.addControl('bankAccountNumberConfirmFormControl', this.bankAccountNumberConfirmFormControl);
        this.paymentForm.addControl('bankAccountNumberEqualsFormControl', this.bankAccountNumberEqualsFormControl);
        this.paymentForm.addControl('bankAccountTypeFormControl', this.bankAccountTypeFormControl);

        this.paymentForm.setValue({
            nameFormControl: this.paymentAddress.name,
            addressLine1FormControl: this.paymentAddress.addressLine1,
            addressLine2FormControl: this.paymentAddress.addressLine2,
            cityFormControl: this.paymentAddress.city,
            countryFormControl: this.paymentAddress.country,
            stateFormControl: this.paymentAddress.state,
            zipCodeFormControl: this.paymentAddress.zipCode,
            paymentTypeFormControl: this.paymentAddress.acceptCreditCardFlag ? PaymentType.CreditCard.valueOf() : PaymentType.ElectronicCheck.valueOf(),
            bankRoutingNumberFormControl: '',
            bankAccountNumberFormControl: '',
            bankAccountNumberConfirmFormControl: '',
            bankAccountNumberEqualsFormControl: true,
            bankAccountTypeFormControl: BankAccountType.Checking.valueOf()
        });

        // Create Braintree's HostedFields when accepting credit cards
        if (this.paymentAddress.acceptCreditCardFlag) {
            this.createBrainTreeHostedFields();
        } else {
            // Only ACH; Don't show Apple Pay Test Buttons
            this.isApplePayBackendTest = false;
            this.browserSupportsApplePay = false;
        }

        // 03/23/2021 RA - Reload State list to include filter by country.
        this.getStateCountryList(this.paymentForm.get('countryFormControl').value);

        // 04/08/2021 RA - Verify the State/Country combination.
        this.verifyState_Country(this.paymentForm.get('stateFormControl').value,
            this.paymentForm.get('countryFormControl').value);

        // 04/13/2021 RA - To verify if the State/Country combination is valid and if not then mark State as invalid
        if (this.verifySt_CntyListComplete === false) {
            this.paymentForm.get('stateFormControl').markAsTouched();
        }

        this.paymentForm.get('nameFormControl').setValidators([Validators.required]);
        this.paymentForm.get('addressLine1FormControl').setValidators([Validators.required]);
        this.paymentForm.get('cityFormControl').setValidators([Validators.required]);
        this.paymentForm.get('stateFormControl').setValidators([Validators.required]);
        this.paymentForm.get('countryFormControl').setValidators([Validators.required]);                // 04/13/2021 RA - Added

        // 03/12/2021 RA - Check the Zip Code validation - passing the country code
        this.setPaymentZipCodeValidation(this.paymentForm.get('countryFormControl').value);

        // 04/13/2021 RA - Mark Country field as touch to trigger validation of the field to make sure it's valid.
        this.paymentForm.get('countryFormControl').markAsTouched();

        // 03/23/2021 RA - Need to update the values after filling the drop down boxes and validate data
        this.paymentForm.get('countryFormControl').updateValueAndValidity();
        this.paymentForm.get('stateFormControl').updateValueAndValidity();
        this.paymentForm.get('zipCodeFormControl').updateValueAndValidity();

        // 03/30/2021 RA - Comment out for new ZipCode validation
        // this.paymentForm
        //  .get('zipCodeFormControl')
        //  .setValidators([
        //    Validators.required,
        //    Validators.minLength(this.fieldValidations.payment.zipCodeMinLength),
        //    Validators.maxLength(this.fieldValidations.payment.zipCodeMaxLength)
        //  ]);

        // TA - 06/06/2023 - Not Necessary
        //this.paymentTypeChanged(this.paymentTypeFormControl.value);

        // TA - 06/06/2023 - Getting called many times for same payment; Just get distinct value and process;
        //                   When switching payment types; We will need to render hosted fields again.
        this.paymentForm.get('paymentTypeFormControl').valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(val => {
                this.paymentTypeChanged(val);
                this._brainTreeLastPaymentType = val;
            });

        this.paymentForm.get('bankAccountNumberFormControl').valueChanges.subscribe(val => {
            this.bankAccountNumberChanged(val);
        });

        this.paymentForm.get('bankAccountNumberConfirmFormControl').valueChanges.subscribe(val => {
            this.bankAccountNumberChanged(val);
        });

        // 03/30/2021 This will add a value change listener to the country form control -
        //            the code inside will get called with the user changes value for country
        // 06/06/2023 - TA - Getting called many times causing call stack to exceed size. Process distinct values.
        this.paymentForm.get('countryFormControl').valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(val => {
                this.setPaymentZipCodeValidation(val);
                this.reset_state(val);
            });

        // 03/30/2021 This will add a value change listener to the zip code form control -
        //            the code inside will get called with the user changes value for zip code
        // 06/06/2023 - TA - Getting called many times causing call stack to exceed size. Process distinct values.
        this.paymentForm.get('zipCodeFormControl').valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(val => {
                this.setPaymentZipCodeValidation(this.paymentForm.get('countryFormControl').value);
            });

        // 03/30/2021 RA - To display <mat-error> content for instance when you click on another control other than the submit,
        //                 simply mark the desired control(s) as touched in the handler of the button:
        this.paymentForm.get('zipCodeFormControl').markAsTouched();
    }

    /**
     * Routine to create the Braintree's credit card hosted fields
     */
    private createBrainTreeHostedFields() {

        let lenderProgramCodes: string[] = this._braintreeService.extractLenderProgramCodes(this.paymentSelected.selectedData);
        this._brainTreeLenderProgramCode = this._braintreeService.getBrainTreeLenderProgramCode(lenderProgramCodes);

        this._paymentService.getBrainTreeClientToken(this._brainTreeLenderProgramCode).subscribe(
            response => {
                this._clientToken = response.clientToken;

                if (this._logging.serviceDataTraceLog) {
                    console.log("Braintree Client Token: " + this._clientToken);
                }
            },
            error => {
                this._handleError('createBrainTreeHostedFields :: getBrainTreeClientToken', error);
            },
            () => {

                // Received client token from Braintree so let's have braintree inject it's IFrames
                this.createBrainTreeUI();
            }
        );
    }

    /**
     * Routine to create the Braintree's UI. Braintree will inject it's Iframes into the html
     */
    private createBrainTreeUI() {
        this.showBraintreeSpinner = true;

        // Need to create the Google Pay button; Important, environment assignment!
        var button = document.querySelector('#google-pay-button');

        // Switching between credit card and electronic check was duplicating Google Pay button.
        // Lets clear the container to avoid duplicate buttons.
        button.innerHTML = "";
        var paymentsClient;

        if (this._googlePayConfig.isTestEnvironment) {
            paymentsClient = new google.payments.api.PaymentsClient({
                environment: "TEST"
            });
        } else {
            paymentsClient = new google.payments.api.PaymentsClient({
                environment: "PRODUCTION"
            });
        }

        const googleButton = paymentsClient.createButton({
            buttonColor: 'black',
            buttonType: 'plain',
            buttonSizeMode: 'fill',
            onClick: () => { }               // Adding listener below, but required for method
        });
        button.appendChild(googleButton);

        // Apple Pay
        //TODO: needs to be SSL for secure document
        if (window.ApplePaySession
            // && window.ApplePaySession.canMakePayments() 
            // && window.ApplePaySession.supportsVersion(this._applePayConfig.version)
        ) {
            this.browserSupportsApplePay = true;
        } else {
            this.browserSupportsApplePay = false;
            console.log("Device doesn't support Apple Pay");
            console.log("Apple Session: " + JSON.stringify(braintree.ApplePaySession));
        }
        var appleButton = document.querySelector('#apple-pay-button');
        console.log("Apple Button DOM: " + JSON.stringify(appleButton));

        // Apple Pay - Backend Testing
        var appleBackendTestButton = document.querySelector('#apple-pay-backend-test-button');
        if (this._applePayConfig.testNonce !== null && this._applePayConfig.testNonce !== '') {
            this.isApplePayBackendTest = true;
        }
        console.log("isAppleBackendTest: " + this.isApplePayBackendTest);
        console.log("apple Backend Test Button: " + appleBackendTestButton);

        // Create the various Braintree payment implementations
        braintree.client.create({
            authorization: this._clientToken,
        }).then((clientInstance: any) => {

            // Device data
            braintree.dataCollector.create({
                client: clientInstance
            }).then((dataCollectorInstance) => {
                this._dataCollectorInstance = dataCollectorInstance;
            });

            // Hosted fields
            braintree.hostedFields.create({
                client: clientInstance,
                //preventAutofill: true,

                // Override styles for the hosted fields
                styles: {
                    'input': {
                        'font-size': '14px',
                        'font-family': 'roboto, "Helvetica Neue", sans-serif',
                        'color': 'rgb(102, 102, 102)',
                    },
                    '.expirationMonth': {
                        'line-height': '3em',
                        'height': '3em',
                    },
                    '.expirationYear': {
                        'line-height': '3em',
                        'height': '3em',
                    }
                },

                // The hosted fields
                fields: {
                    number: {
                        container: '#credit-card-number',
                    },
                    expirationMonth: {
                        container: '#expiration-month',
                        select: true,
                    },
                    expirationYear: {
                        container: '#expiration-year',
                        select: true,
                    },
                    cvv: {
                        container: '#cvv',
                    },
                }

            }).then((hostedFieldsInstance) => {
                this._hostedFieldsInstance = hostedFieldsInstance;

                // Braintree provides months; however, numeric values not prefixed with zero
                (hostedFieldsInstance as any).setMonthOptions(this.creditCardExpMonthList);

                // TODO - Not being applied
                hostedFieldsInstance.addClass('expirationMonth', 'to-expiration-date', (addClassError) => {
                    if (addClassError) {
                        console.error(addClassError);
                    }
                });

                hostedFieldsInstance.on('focus', (event) => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('PaymentFormComponent.createBrainTreeUI :: onFocus Emitted By = ' + event.emittedBy);
                    }
                    const field = event.fields[event.emittedBy];
                    const emittedBy = event.emittedBy;
                    this._brainTreeFieldFocus = event.emittedBy;

                    this._braintreeService.onFocus(field, emittedBy);
                });

                hostedFieldsInstance.on('blur', (event) => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('PaymentFormComponent.createBrainTreeUI :: onBlur Emitted By = ' + event.emittedBy);
                    }
                    const field = event.fields[event.emittedBy];
                    const emittedBy = event.emittedBy;

                    this._braintreeService.onBlur(field, emittedBy);

                    // Determine if we can enable 'Submit Payment' button; With the combination of Material and HTML objects,
                    // we must programmatically set the disable property. Injected IFrames not part of the Reactive Forms so
                    // the disabled property is not bound to the form control to be updated.
                    this._brainTreeFieldsValid = this._braintreeService.isBraintreeFieldsValid(this._hostedFieldsInstance);
                    this._braintreeService.enableDisablePaymentButton(this.SubmitButton.nativeElement,
                        this._brainTreeFieldsValid,
                        this.paymentForm.valid);
                    this.cdr.detectChanges();
                });

                hostedFieldsInstance.on('empty', (event) => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('PaymentFormComponent.createBrainTreeUI :: onEmpty Emitted By = ' + event.emittedBy);
                    }
                    const field = event.fields[event.emittedBy];

                    this._braintreeService.onEmpty(field);
                });

                hostedFieldsInstance.on('validityChange', (event) => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('PaymentFormComponent.createBrainTreeUI :: onValidityChange Emitted By = ' + event.emittedBy);
                    }
                    const field = event.fields[event.emittedBy];
                    const emittedBy = event.emittedBy;

                    this._braintreeService.onValidityChange(field, emittedBy, this._brainTreeFieldFocus);

                    // Determine if we can enable 'Submit Payment' button; With the combination of Material and HTML objects,
                    // we must programmatically set the disable property. Injected IFrames not part of the Reactive Forms so
                    // the disabled property is not bound to the form control to be updated.
                    this._brainTreeFieldsValid = this._braintreeService.isBraintreeFieldsValid(this._hostedFieldsInstance);
                    this._braintreeService.enableDisablePaymentButton(this.SubmitButton.nativeElement,
                        this._brainTreeFieldsValid,
                        this.paymentForm.valid);
                    this.cdr.detectChanges();

                });

                this._brainTreeUIRenderComplete = true;
                this.showBraintreeSpinner = false;

                if (this._logging.serviceCallTraceLog) {
                    console.log("Braintree UI Complete - Render Complete:" + this._brainTreeUIRenderComplete);
                }

            }).catch((error) => {
                this._handleError('createBrainTreeUI :: hostedFields.create', error);
            });

            //   // Paypal
            //   braintree.paypalCheckout.create({
            //     client: clientInstance
            //   })
            //   .then((paypalCheckoutInstance) => {
            //       this._payPalCheckoutInstance = paypalCheckoutInstance;

            //       return paypalCheckoutInstance.loadPayPalSDK({vault: true})

            //       .then((paypalCheckoutInstance) => {
            //           console.log("Paypal is now loaded");
            //           console.log("window.paypal.Buttons is now available to use");

            //           return window.paypal.Buttons({
            //             fundingSource: window.paypal.FUNDING.PAYPAL.toString(),

            //             style: {
            //               color: ButtonColorOption.Silver,
            //               size: ButtonSizeOption.Responsive,
            //               disableMaxWidth: true,
            //               disableMaxHeight: true,
            //             },
            //             createBillingAgreement: () => {
            //               return paypalCheckoutInstance.createPayment({
            //                 flow: FlowType.Vault,
            //                 enableShippingAddress: false,
            //                 shippingAddressEditable: false,
            //               })
            //             },
            //             onApprove: (data, _actions) => {
            //               console.log("Approved: PayPal tokenization");
            //               data.vault = true;
            //               return paypalCheckoutInstance.tokenizePayment(data)
            //                       .then((payload) => {
            //                           if (this._logging.serviceDataTraceLog) {
            //                               console.log("Submit payload.nonce to server " + payload.nonce);
            //                               console.log("Payload: " + JSON.stringify(payload.details));
            //                               console.log("Payload: " + payload.type);
            //                           }

            //                           const paymentDetail = this.getPaymentDetail(PaymentType.PayPal);
            //                           paymentDetail.nonce = payload.nonce;
            //                           paymentDetail.partiFirstName = this._userService.getFirstName();
            //                           paymentDetail.partiLastName = this._userService.getLastName();
            //                           paymentDetail.partiEmail = this._userService.getEmail();
            //                           paymentDetail.deviceData = this._dataCollectorInstance.deviceData;

            //                           paymentDetail.brainTreePaymentType = payload.type;
            //                           paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

            //                           if (this._logging.serviceDataTraceLog) {
            //                             console.log("Paypal Payment.Form:PaymentDetail: " + JSON.stringify(paymentDetail));
            //                           }

            //                           this.submitted.emit(paymentDetail);
            //                       });
            //             },
            //             onCancel: (data, _actions) => {
            //                 console.log("Paypal Cancel", JSON.stringify(data));
            //             },
            //             onError(error) {
            //               console.log("Paypal Error: " + error);
            //             },
            //           }).render('#paypal-button');
            //       })
            //       .catch((err) => {
            //         console.log("Issue loading the PayPal JS SDK: " + err);
            //       })
            //   })
            //   .catch((err) => {
            //     console.log("Issue with Paypal component creation: " + err);
            //   });

            // Google Pay
            braintree.googlePayment.create({
                client: clientInstance,
                googlePayVersion: 2,                                        // Always version 2
                googleMerchantId: this._googlePayConfig.googleMerchantId
            }).then((googlePaymentInstance) => {
                this._googlePayInstance = googlePaymentInstance;

                return paymentsClient.isReadyToPay({
                    apiVersion: 2,                                          // Always version 2
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                        {
                            "type": "CARD",
                            "parameters": {
                                "allowedAuthMethods": ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                                "allowedCardNetworks": ["AMEX", "DISCOVER", "MASTERCARD", "VISA"]
                            }
                        }
                    ],
                    // Braintree documentation shows the following statement but is not correct
                    //allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods,
                    existingPaymentMethodRequired: true,
                });
            }).then((isReadyToPay: any) => {

                if (isReadyToPay.result) {
                    if (this._logging.serviceDataTraceLog) {
                        console.log("Googgle Pay:isReadyToPay: " + JSON.stringify(isReadyToPay));
                    }

                    // Set up Google Pay Button
                    button.addEventListener('click', async (event) => {
                        event.preventDefault();

                        var paymentDataRequest = this._googlePayInstance.createPaymentDataRequest({
                            transactionInfo: {
                                currencyCode: 'USD',
                                totalPriceStatus: 'FINAL',
                                totalPrice: this._paymentAmount.toFixed(2),
                            }
                        });

                        var cardPaymentMethod = (await paymentDataRequest).allowedPaymentMethods[0];
                        cardPaymentMethod.parameters.billingAddressRequired = true;
                        cardPaymentMethod.parameters.billingAddressParameters = {
                            format: 'FULL',
                            phoneNumberRequired: false
                        };

                        paymentsClient.loadPaymentData(await paymentDataRequest).then((paymentData) => {
                            return this._googlePayInstance.parseResponse(paymentData);
                        }).then((result) => {
                            if (this._logging.serviceDataTraceLog) {
                                console.log("Google Pay:Payload: " + JSON.stringify(result));
                            }

                            // Issue call to back-end server
                            const paymentDetail = this.getPaymentDetail(PaymentType.GooglePay);
                            paymentDetail.nonce = result.nonce;
                            paymentDetail.partiFirstName = this._userService.getFirstName();
                            paymentDetail.partiLastName = this._userService.getLastName();
                            paymentDetail.partiEmail = this._userService.getEmail();
                            paymentDetail.deviceData = this._dataCollectorInstance.deviceData;

                            paymentDetail.brainTreePaymentType = result.type;
                            paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

                            if (this._logging.serviceDataTraceLog) {
                                console.log("Googgle Pay Payment.Form:PaymentDetail: " + JSON.stringify(paymentDetail));
                            }

                            this.submitted.emit(paymentDetail);

                        }).catch((err) => {
                            console.log("Google Pay loadPaymentData Error: " + JSON.stringify(err));
                        });

                        // For testing - Use testNonce from Braintree Testing Documentation - Issue Backend call without having Google Account with valid credit card
                        if (this._googlePayConfig.testNonce !== null && this._googlePayConfig.testNonce !== '') {

                            const paymentDetail = this.getPaymentDetail(PaymentType.GooglePay);
                            paymentDetail.nonce = this._googlePayConfig.testNonce;
                            paymentDetail.partiFirstName = this._userService.getFirstName();
                            paymentDetail.partiLastName = this._userService.getLastName();
                            paymentDetail.partiEmail = this._userService.getEmail();
                            paymentDetail.deviceData = this._dataCollectorInstance.deviceData;

                            paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

                            this.submitted.emit(paymentDetail);
                        }

                    });
                } else {
                    console.error("Google Pay buttion is not in a ready state!");
                }
            }).catch((err) => {
                console.error("Google Pay error: " + JSON.stringify(err));
            });

            // Apple Pay
            if (this.browserSupportsApplePay) {
                braintree.applePay.create({
                    client: clientInstance
                }).then((applePayInstance) => {
                    this._applePayInstance = applePayInstance;
                    if (this._logging.serviceDataTraceLog) {
                        console.log("Apple Pay Instance: " + JSON.stringify(applePayInstance));
                    }

                    // Obtain company name to be used on 1st sheet shown by Apple
                    var companyName = this.companyInformation.companyName;

                    appleButton.addEventListener('click', async (event) => {
                        event.preventDefault();

                        var paymentRequest = applePayInstance.createPaymentRequest({
                            total: {
                                label: companyName + ' - ' + this._applePayConfig.label,
                                amount: this._paymentAmount.toFixed(2),
                            },
                            requiredBillingContactFields: ["postalAddress"]
                        });
                        if (this._logging.serviceDataTraceLog) {
                            console.log("Apple Pay - paymentRequest: " + JSON.stringify(paymentRequest));
                        }

                        var applePaySession = new window.ApplePaySession(this._applePayConfig.version, paymentRequest);

                        // Callback to create a validated Apple Pay session
                        applePaySession.onvalidatemerchant = function (event) {
                            applePayInstance.performValidation({
                                validationURL: event.validationURL,
                                displayName: companyName
                            }).then((merchantSession) => {
                                applePaySession.completeMerchantValidation(merchantSession);
                                console.log("merchant validated: " + JSON.stringify(applePaySession));
                            }).catch((validationErr) => {
                                console.error("Apple Pay: Error validating merchant:", validationErr);
                                applePaySession.abort();
                            });
                        }

                        // Safari will call this callback with a token which is used by Braintree
                        applePaySession.onpaymentauthorized = (event) => {

                            applePayInstance.tokenize({
                                token: event.payment.token
                            }).then((payLoad) => {
                                if (this._logging.serviceDataTraceLog) {
                                    console.log("Apple Pay: Payload: " + JSON.stringify(payLoad));
                                }

                                // Issue call to back-end server
                                const paymentDetail = this.getPaymentDetail(PaymentType.ApplePay);
                                paymentDetail.nonce = payLoad.nonce;
                                paymentDetail.partiFirstName = this._userService.getFirstName();
                                paymentDetail.partiLastName = this._userService.getLastName();
                                paymentDetail.partiEmail = this._userService.getEmail();
                                paymentDetail.deviceData = this._dataCollectorInstance.deviceData;

                                paymentDetail.brainTreePaymentType = payLoad.type;
                                paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

                                if (this._logging.serviceDataTraceLog) {
                                    console.log("Apple Pay: Payment.Form:PaymentDetail: " + JSON.stringify(paymentDetail));
                                }
                                applePaySession.completePayment(window.ApplePaySession.STATUS_SUCCESS);

                                this.submitted.emit(paymentDetail);
                            }).catch((tokenizeError) => {
                                console.error("Error tokenizing Apple Pay:", tokenizeError);
                                applePaySession.completePayment(window.ApplePaySession.STATUS_FAILURE);
                            });
                        }

                        // Show Payment Sheet
                        applePaySession.begin();

                    });

                }).catch((err) => {
                    console.error("Issue creating apple pay instance: " + err);
                })
            }

            // Apple Backend Testing - Hack - Need to select note twice; 1st time button element not available
            if (this.isApplePayBackendTest && appleBackendTestButton != null) {
                console.log("Testing nonce: " + this._applePayConfig.testNonce);
                console.log("Testing Apple Pay Button Available: " + appleBackendTestButton);

                appleBackendTestButton.addEventListener('click', (event) => {
                    event.preventDefault();

                    const paymentDetail = this.getPaymentDetail(PaymentType.ApplePay);
                    paymentDetail.nonce = this._applePayConfig.testNonce;
                    paymentDetail.partiFirstName = this._userService.getFirstName();
                    paymentDetail.partiLastName = this._userService.getLastName();
                    paymentDetail.partiEmail = this._userService.getEmail();
                    paymentDetail.deviceData = this._dataCollectorInstance.deviceData;

                    paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

                    this.submitted.emit(paymentDetail);
                });
            }
        }); // End clientInstance
    }

    // Routine to dump object during testing.  Google Pay didn't play nice with
    // console.log used directly within the promises.
    private dumpToConsole(messagePrefix: string, obj: any) {
        if (this._logging.serviceDataTraceLog) {
            console.log(messagePrefix + ": " + obj);
        }
    }

    // 03/18/2021 RA - Add method to reload the state field filtered by country
    reset_state(newcountry: string) {
        this.paymentForm.controls['stateFormControl'].setValue(this.getStateCountryList(newcountry));
        this.paymentForm.controls['stateFormControl'].markAsDirty();
        this.paymentForm.controls['stateFormControl'].markAsTouched();
        this.paymentForm.controls['stateFormControl'].updateValueAndValidity();

        // 04/13/2021 RA
        this.paymentForm.controls['zipCodeFormControl'].markAsDirty();
        this.paymentForm.controls['zipCodeFormControl'].markAsTouched();
        this.paymentForm.controls['zipCodeFormControl'].updateValueAndValidity();
    }

    // 03/12/2021 RA - setPaymentZipCodeValidation will set the validators for Payment zip code based on the provided country.
    setPaymentZipCodeValidation(country: string = null): void {
        const validators: ValidatorFn[] = [
            Validators.minLength(this.getPaymentZipCodeMinLength(country)),
            Validators.maxLength(this.getPaymentZipCodeMaxLength(country))
        ];

        // Validators.required(),   -- 03/26/2021 RA Removed to do below
        // 03/29/2021 RA - Add new Zip code required validation only for USA and CAN.
        if (country === 'USA' || country === 'CAN') {
            validators.push(Validators.required);
        } else {
            this.paymentForm.get('zipCodeFormControl').clearValidators();
        }

        // 03/30/2021 RA - A valid canadian postcode is 7 digits –
        //   in the format A1A 1A1, where A is a letter and 1 is a digit.
        //   a space separates the third and fourth characters.
        //   do not include the letters D, F, I, O, Q or U.
        //   the first position does not make use of the letters W or Z.
        // 03/30/2021 RA - Added Foreign Regex to be any Alphanumeric or Numeric or Blank
        //
        if (country !== null && country.trim().toUpperCase() === 'CAN') {
            const canadianZipCodeRegex = /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/;
            validators.push(Validators.pattern(canadianZipCodeRegex));
        }

        if (country !== null && country.trim().toUpperCase() === 'USA') {
            const usaZipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
            validators.push(Validators.pattern(usaZipCodeRegex));
        }

        if (country !== null && country.trim().toUpperCase() === 'FOR') {
            const forZipCodeRegex = /^([a-zA-Z0-9][\s\\/-]*){0,10}$/;
            validators.push(Validators.pattern(forZipCodeRegex));
        }
        this.paymentForm.controls['zipCodeFormControl'].markAsDirty();
        this.paymentForm.get('zipCodeFormControl').setValidators(validators);
        this.paymentForm.get('zipCodeFormControl').updateValueAndValidity();
    }

    // 03/30/2021 RA - PaymentZipCodeOnlyAllowsNumbers checks to see if zip code entry allows numbers (used in the HTML file)
    PaymentZipCodeOnlyAllowsNumbers(): boolean {
        if (this.paymentForm.get('countryFormControl').value !== null &&
            String(this.paymentForm.get('countryFormControl').value).toUpperCase() !== 'USA') {
            return false;
        } else {
            return true;
        }
    }

    // 03/30/2021 RA - get ZipCodeMinLength returns the min zip code length based on country
    // 03/30/2021 RA - Broke out Foreign into its own category
    getPaymentZipCodeMinLength(country: string = null): number {
        if (country !== null) {
            if (country.trim().toUpperCase() === 'USA') {
                return this.fieldValidations.contact.zipCodeMinLength_USA;
            } else if (country.trim().toUpperCase() === 'CAN') {
                return this.fieldValidations.contact.zipCodeMinLength_CAN;
            } else if (country.trim().toUpperCase() === 'FOR') {
                return this.fieldValidations.contact.zipCodeMinLength_FOR;
            } else {
                return this.fieldValidations.contact.zipCodeMinLength;
            }
        } else {
            return this.fieldValidations.contact.zipCodeMinLength;
        }
    }

    // 03/30/2021 RA - getPaymentZipCodeMaxLength returns the max zip code length based on country
    // 03/30/2021 RA - Broke out Foreign into its own category
    getPaymentZipCodeMaxLength(country: string = null): number {
        if (country !== null) {
            if (country.trim().toUpperCase() === 'USA') {
                return this.fieldValidations.contact.zipCodeMaxLength_USA;
            } else if (country.trim().toUpperCase() === 'CAN') {
                return this.fieldValidations.contact.zipCodeMaxLength_CAN;
            } else if (country.trim().toUpperCase() === 'FOR') {
                return this.fieldValidations.contact.zipCodeMaxLength_FOR;
            } else {
                return this.fieldValidations.contact.zipCodeMaxLength;
            }
        } else {
            return this.fieldValidations.contact.zipCodeMaxLength;
        }
    }

    paymentTypeChanged(newPaymentType: PaymentType) {
        this.paymentForm.get('bankRoutingNumberFormControl').clearValidators();
        this.paymentForm.get('bankAccountNumberFormControl').clearValidators();
        this.paymentForm.get('bankAccountNumberConfirmFormControl').clearValidators();
        this.paymentForm.get('bankAccountNumberEqualsFormControl').clearValidators();
        this.paymentForm.get('bankAccountTypeFormControl').clearValidators();

        if (newPaymentType === PaymentType.ElectronicCheck) {
            this.paymentForm
                .get('bankRoutingNumberFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.payment.bank.bankRoutingNumberMinLength),
                    Validators.maxLength(this.fieldValidations.payment.bank.bankRoutingNumberMaxLength)
                ]);
            this.paymentForm
                .get('bankAccountNumberFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.payment.bank.bankAccountNumberMinLength),
                    Validators.maxLength(this.fieldValidations.payment.bank.bankAccountNumberMaxLength)
                ]);
            this.paymentForm
                .get('bankAccountNumberConfirmFormControl')
                .setValidators([
                    Validators.required,
                    Validators.minLength(this.fieldValidations.payment.bank.bankAccountNumberMinLength),
                    Validators.maxLength(this.fieldValidations.payment.bank.bankAccountNumberMaxLength)
                ]);
            this.paymentForm.get('bankAccountNumberEqualsFormControl').setValidators([Validators.requiredTrue]);
        } else {

            // Create Braintree's HostedFields. Needed to recreate when switching between CC and electronic payment
            // Routine is called when changing payment or selecting another note so lets insure we are on payment form switching types.
            if (this._brainTreeUIRenderComplete && this._brainTreeLastPaymentType === PaymentType.ElectronicCheck) {
                this._brainTreeUIRenderComplete = false;
                this._braintreeService.tearDownBraintree(this._hostedFieldsInstance);
                this.createBrainTreeHostedFields();
            }
        }

        this.paymentForm.get('bankRoutingNumberFormControl').updateValueAndValidity();
        this.paymentForm.get('bankAccountNumberFormControl').updateValueAndValidity();
        this.paymentForm.get('bankAccountNumberConfirmFormControl').updateValueAndValidity();
        this.paymentForm.get('bankAccountNumberEqualsFormControl').updateValueAndValidity();
        this.paymentForm.get('bankAccountTypeFormControl').updateValueAndValidity();

    }

    bankAccountNumberChanged(newBankAccountNumberConfirm: string) {
        this.paymentForm.controls['bankAccountNumberEqualsFormControl'].setValue(this.areBankAccountNumbersEqual());
        this.paymentForm.controls['bankAccountNumberEqualsFormControl'].markAsDirty();
        this.paymentForm.controls['bankAccountNumberEqualsFormControl'].markAsTouched();
        this.paymentForm.get('bankAccountNumberEqualsFormControl').updateValueAndValidity();
    }

    submitPayment(): void {

        // With the mixture of Reactive form controls & Non-Reactive form controls (Hosted Fields), rapid clicks was not disabling button fast enough
        this.SubmitButton.nativeElement.disabled = true;
        if (this._isSubmitted) {
            return;
        }
        this._isSubmitted = true;

        const paymentDetail = this._paymentService.getNewPaymentDetail();
        paymentDetail.participantId = this._participantId;
        paymentDetail.noteIds = this.noteIds.join(', ');
        paymentDetail.name = this.paymentForm.controls['nameFormControl'].value;
        paymentDetail.addressLine1 = this.paymentForm.controls['addressLine1FormControl'].value;
        paymentDetail.addressLine2 = this.paymentForm.controls['addressLine2FormControl'].value;
        paymentDetail.city = this.paymentForm.controls['cityFormControl'].value;
        paymentDetail.state = this.paymentForm.controls['stateFormControl'].value;
        paymentDetail.zipCode = this.paymentForm.controls['zipCodeFormControl'].value;
        paymentDetail.paymentAmount = this._paymentAmount;
        paymentDetail.paymentDueDay = this._paymentSelected.selectedData[0].paymentDueDay;

        // Obtained from store proc str_sel_payment_info - Not used
        paymentDetail.loanProgram = this._paymentSelected.selectedData[0].loanProgram;

        // Required to control application flow (i.e. electronic payment)
        paymentDetail.paymentType = this.paymentForm.controls['paymentTypeFormControl'].value;

        paymentDetail.bankRoutingNumber = this.paymentForm.controls['bankRoutingNumberFormControl'].value;
        paymentDetail.bankAccountNumber = this.paymentForm.controls['bankAccountNumberFormControl'].value;
        paymentDetail.bankAccountConfirmNumber = this.paymentForm.controls['bankAccountNumberConfirmFormControl'].value;
        paymentDetail.bankAccountType = this.paymentForm.controls['bankAccountTypeFormControl'].value;
        paymentDetail.userName = this._userService.getUserName();
        paymentDetail.paymentRef = null;
        paymentDetail.checkPaymentId = null;
        paymentDetail.confirmationCode = null;
        paymentDetail.responseCode = null;
        paymentDetail.responseMessage = null;

        // Credit Card information needs to be tokenized via Braintree API
        if (this.isPaymentTypeCreditCard()) {
            if (this._hostedFieldsInstance == null) {
                var error = { errDesc: "HostedFieldsInstance is null. Can't create Braintree CC properties" };
                this._handleError('submitPayment', error);
            }
            this._hostedFieldsInstance.tokenize((tokenizeErr, payload) => {
                if (tokenizeErr) {
                    this._handleError('submitPayment', tokenizeErr, payload);
                } else {

                    if (this._logging.serviceDataTraceLog) {
                        console.log("Braintree tokenized payload: " + JSON.stringify(payload));
                    }
                    paymentDetail.nonce = payload.nonce;
                    paymentDetail.partiFirstName = this._userService.getFirstName();
                    paymentDetail.partiLastName = this._userService.getLastName();
                    paymentDetail.partiEmail = this._userService.getEmail();
                    paymentDetail.deviceData = this._dataCollectorInstance.deviceData;
                    paymentDetail.creditCardType = payload.details.cardType;
                    paymentDetail.lastFourCreditCard = payload.details.lastFour;

                    // Not consistent with providing leading zero for single digits
                    paymentDetail.creditCardExpMonth = ('0' + payload.details.expirationMonth).slice(-2);
                    paymentDetail.creditCardExpYear = payload.details.expirationYear;
                    paymentDetail.creditCardBin = payload.details.bin;
                    paymentDetail.brainTreePaymentType = payload.type;
                    paymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;
                    paymentDetail.servicingCompany = this._braintreeService.getServicingCompany(payload.details.cardType);

                    if (this._logging.serviceDataTraceLog) {
                        console.log("Payment.Form:PaymentDetail: " + JSON.stringify(paymentDetail));
                    }

                    this.submitted.emit(paymentDetail);
                }
            });
        } else {
            this.submitted.emit(paymentDetail);
        }

    }

    cancelPayment(): void {
        this.canceled.emit();
    }

    isPaymentTypeCreditCard(): boolean {
        return !(this.paymentForm.controls['paymentTypeFormControl'].value === PaymentType.ElectronicCheck);
    }

    areBankAccountNumbersEqual(): boolean {
        return (
            this.paymentForm.controls['bankAccountNumberFormControl'].value ===
            this.paymentForm.controls['bankAccountNumberConfirmFormControl'].value
        );
    }

    // 10/11/2021 - Get Company Code
    CompanyCode_Check(): boolean {
        for (const companyLookup of environment.companyCode.legaltext) {
            if (companyLookup.companyCode === this.companyCode) {
                return true;
            }
        }
        return false;
    }

    // Validate Angular form controls and Braintree fields
    isFormValid() {

        if (this.isPaymentTypeCreditCard()) {
            return !(this._brainTreeFieldsValid && this.paymentForm.valid);
        } else {
            return !(this.paymentForm.valid);
        }
    }

    // Routine to create a PaymentDetail object
    getPaymentDetail(paymentType: PaymentType): PaymentDetail {
        const paymentDetail = this._paymentService.getNewPaymentDetail();
        paymentDetail.participantId = this._participantId;
        paymentDetail.noteIds = this.noteIds.join(', ');
        paymentDetail.name = this.paymentForm.controls['nameFormControl'].value;
        paymentDetail.addressLine1 = this.paymentForm.controls['addressLine1FormControl'].value;
        paymentDetail.addressLine2 = this.paymentForm.controls['addressLine2FormControl'].value;
        paymentDetail.city = this.paymentForm.controls['cityFormControl'].value;
        paymentDetail.state = this.paymentForm.controls['stateFormControl'].value;
        paymentDetail.zipCode = this.paymentForm.controls['zipCodeFormControl'].value;
        paymentDetail.paymentAmount = this._paymentAmount;
        paymentDetail.paymentDueDay = this._paymentSelected.selectedData[0].paymentDueDay;

        // Obtained from store proc str_sel_payment_info - Not used
        paymentDetail.loanProgram = this._paymentSelected.selectedData[0].loanProgram;

        // Required to control application flow (i.e. electronic payment)
        paymentDetail.paymentType = paymentType;

        paymentDetail.userName = this._userService.getUserName();
        paymentDetail.paymentRef = null;
        paymentDetail.checkPaymentId = null;
        paymentDetail.confirmationCode = null;
        paymentDetail.responseCode = null;
        paymentDetail.responseMessage = null;

        return paymentDetail;
    }
}
